import { NavLink } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/design/cn";
import { ImpactLedgerMark } from "./ImpactLedgerLogo";
import { useAuthStore } from "@/domain/authStore";
import { useRoleGuide, RoleGuideTriggerIcon } from "./RoleGuideProvider";

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: number;
  /** One-line "what this does" - not rendered in the sidebar itself, only
   * consumed by the role guide's "Where to find things" list
   * (RoleGuideProvider.tsx), which is why it's optional here. */
  description?: string;
};

export type NavSection = {
  title?: string;
  items: NavItem[];
};

// Brand signal colour — matches var(--il-signal) / #535EFF
const SIGNAL = "#535EFF";
const BRAND = "#272FA0";

export function Sidebar({
  context,
  sections,
  showBrand = true,
  className,
}: {
  context: string;
  sections: NavSection[];
  showBrand?: boolean;
  className?: string;
}) {
  const user = useAuthStore((s) => s.user);
  const { openGuide } = useRoleGuide();
  const initials = user
    ? `${user.firstname?.[0] ?? ""}${user.lastname?.[0] ?? ""}`.toUpperCase()
    : "??";
  const displayName = user ? `${user.firstname} ${user.lastname}` : "";

  return (
    <aside
      className={cn("relative flex w-[248px] shrink-0 flex-col overflow-hidden", className)}
      style={{ background: "linear-gradient(180deg, #0D1128 0%, #080B1A 100%)" }}
    >
      {/* Top-left brand glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-8 -top-8 h-48 w-48 rounded-full opacity-30 blur-3xl"
        style={{ background: BRAND }}
      />

      {/* ── Brand header ─────────────────────────────────────────────────── */}
      <div className="relative z-10 flex items-center gap-3 px-4 py-[18px]">
        {showBrand && (
          <>
            {/* Logo box */}
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
              style={{
                background: "linear-gradient(135deg, #272FA0 0%, #535EFF 100%)",
                boxShadow: `0 0 20px ${SIGNAL}55`,
              }}
            >
              <ImpactLedgerMark size={18} onInk />
            </div>

            <div className="min-w-0">
              <p className="text-[14px] font-bold tracking-tight text-white">Impact Ledger</p>
              <p className="text-[11px] font-medium tracking-wide" style={{ color: "rgba(255,255,255,0.38)" }}>
                {context.toUpperCase()}
              </p>
            </div>
          </>
        )}
        {!showBrand && (
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.3)" }}>
            {context}
          </p>
        )}
      </div>

      {/* Thin separator */}
      <div className="relative z-10 mx-4 mb-2 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />

      {/* ── Navigation ───────────────────────────────────────────────────── */}
      <nav className="relative z-10 flex-1 overflow-y-auto px-3 py-1">
        {sections.map((section, si) => (
          <div key={si} className={cn(si > 0 && "mt-2 pt-2")}>
            {si > 0 && (
              <div className="mx-1 mb-2 h-px" style={{ background: "rgba(255,255,255,0.05)" }} />
            )}
            {section.title && (
              <p
                className="mb-1 px-2 text-[10px] font-bold uppercase tracking-widest"
                style={{ color: "rgba(255,255,255,0.22)" }}
              >
                {section.title}
              </p>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className="group relative flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                  >
                    {({ isActive }) => (
                      <>
                        {/* Active background with glow */}
                        {isActive ? (
                          <span
                            aria-hidden
                            className="absolute inset-0 rounded-xl"
                            style={{
                              background: `linear-gradient(90deg, ${SIGNAL}28 0%, ${SIGNAL}12 100%)`,
                              boxShadow: `inset 0 0 0 1px ${SIGNAL}30`,
                            }}
                          />
                        ) : (
                          <span
                            aria-hidden
                            className="absolute inset-0 rounded-xl opacity-0 transition-opacity group-hover:opacity-100"
                            style={{ background: "rgba(255,255,255,0.04)" }}
                          />
                        )}

                        {/* Left accent bar (active only) */}
                        {isActive && (
                          <span
                            aria-hidden
                            className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full"
                            style={{
                              background: SIGNAL,
                              boxShadow: `0 0 8px ${SIGNAL}`,
                            }}
                          />
                        )}

                        {/* Icon */}
                        <item.icon
                          aria-hidden
                          className="relative h-[18px] w-[18px] shrink-0 transition-all duration-150"
                          style={{
                            color: isActive ? SIGNAL : "rgba(255,255,255,0.36)",
                            filter: isActive ? `drop-shadow(0 0 6px ${SIGNAL}88)` : "none",
                          }}
                        />

                        {/* Label */}
                        <span
                          className="relative flex-1 truncate text-[13px] transition-colors duration-150"
                          style={{
                            color: isActive ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.48)",
                            fontWeight: isActive ? 600 : 500,
                            letterSpacing: isActive ? "-0.01em" : "0",
                          }}
                        >
                          {item.label}
                        </span>

                        {/* Badge */}
                        {item.badge !== undefined && item.badge > 0 && (
                          <span
                            className="relative rounded-full px-1.5 py-px text-[10px] font-bold tabular-nums"
                            style={{
                              background: "rgba(201,162,75,0.15)",
                              color: "#C9A24B",
                              border: "1px solid rgba(201,162,75,0.35)",
                              boxShadow: "0 0 6px rgba(201,162,75,0.2)",
                            }}
                          >
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* ── Guide trigger — not a nav destination, so no active state; a
          dimmer idle color keeps it from reading as an 8th route. ───────── */}
      <div className="relative z-10 px-3 pb-1">
        <button
          onClick={openGuide}
          className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 transition-colors duration-150 hover:bg-white/[0.04]"
        >
          <RoleGuideTriggerIcon
            aria-hidden
            className="h-[18px] w-[18px] shrink-0 transition-colors duration-150"
            style={{ color: "rgba(255,255,255,0.32)" }}
          />
          <span
            className="text-[13px] font-medium transition-colors duration-150"
            style={{ color: "rgba(255,255,255,0.4)" }}
          >
            Guide
          </span>
        </button>
      </div>

      {/* ── User profile footer ───────────────────────────────────────────── */}
      <div className="relative z-10">
        <div className="mx-4 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
        <div className="flex items-center gap-3 px-4 py-3.5">
          {/* Avatar */}
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold text-white"
            style={{
              background: `linear-gradient(135deg, ${BRAND} 0%, ${SIGNAL} 100%)`,
              boxShadow: `0 2px 8px ${SIGNAL}44`,
            }}
          >
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-semibold text-white/80">{displayName || "Guest"}</p>
            <p className="text-[10px]" style={{ color: "rgba(255,255,255,0.3)" }}>
              {context}
            </p>
          </div>
          {/* Online dot */}
          <span
            className="h-2 w-2 rounded-full shrink-0"
            style={{ background: "#39D559", boxShadow: "0 0 6px #39D559" }}
          />
        </div>
      </div>
    </aside>
  );
}
